import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  CpuChipIcon,
  ArrowTrendingDownIcon,
  Bars3BottomLeftIcon,
  ShoppingCartIcon,
  TruckIcon,
  ShieldCheckIcon,
  ArrowLeftStartOnRectangleIcon,
  InboxIcon,
  UserGroupIcon,
  LockClosedIcon,
  ClockIcon,
  ComputerDesktopIcon,
  KeyIcon,
  WrenchScrewdriverIcon,
  ArchiveBoxIcon,
  CubeIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  InboxArrowDownIcon,
  AcademicCapIcon,
  ChartBarIcon,
  PaperAirplaneIcon,
  UserIcon,
  UserMinusIcon,
  UserPlusIcon,
  Bars3Icon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  SignalIcon,
  UsersIcon,
  XMarkIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ClipboardDocumentListIcon,
  ScaleIcon,
  ArrowsRightLeftIcon,
  LifebuoyIcon,
  SparklesIcon,
  MapPinIcon,
  FaceFrownIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  BriefcaseIcon,
  IdentificationIcon,
  ReceiptPercentIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';

// Placeholders — non-clickable "coming soon" items, shown greyed-out until
// they're real features.
// Inbox is pinned to the very top of the nav (rendered first, below). Every
// signed-in user can receive a broadcast, so it sits outside the department
// gating. Internal route — the dashboard owns '/'.
const INBOX_ROUTE = { name: 'Inbox', icon: InboxIcon, path: '/messages' };

// Real, always-visible regardless of department access — training applies
// company-wide (e.g. HR induction), not just to Training dept members, so
// this deliberately sits outside the DEPARTMENTS/filterDepartmentsByAccess
// gating below entirely. Filtered out if the env var isn't set, same
// convention as every department item's own url.
const ALWAYS_VISIBLE_ITEMS = [
  { name: 'My Learning', icon: AcademicCapIcon, url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/my-learning` : null },
].filter((i) => i.url);

// Company directory — for everyone, so it sits with the always-visible items
// rather than under a department. It's an internal route (the dashboard owns
// '/'), not an external module URL, hence `path` instead of `url`.
const ALWAYS_VISIBLE_ROUTES = [
  { name: 'Directory', icon: UsersIcon, path: '/directory' },
  // Anybody in the company can raise a ticket, so this is ungated like the
  // Directory. It was the greyed-out 'Helpdesk' placeholder in the satellite
  // rails until the feature behind it existed.
  { name: 'Helpdesk', icon: LifebuoyIcon, path: '/helpdesk' },
];

// Department -> tools nav. V1: static list, every logged-in user sees every
// department. Entra group-based (or in-app managed) visibility is a planned
// follow-up, not yet built.
export const DEPARTMENTS = [
  {
    key: 'it',
    name: 'IT',
    fullName: 'IT (Information Technology)',
    icon: CpuChipIcon,
    description: 'Internal tools and engineering resources.',
    items: [
      {
        name: 'Nano User Permissions',
        description: 'Control who has access to each Nano department and module.',
        icon: KeyIcon,
        url: process.env.REACT_APP_NEO2_PERMISSIONS_URL,
        group: 'Administration',
      },
      {
        name: 'BrightHR Leaver Check',
        description: 'Enabled Entra accounts not showing as active in BrightHR — possible missed leavers.',
        icon: UserGroupIcon,
        url: process.env.REACT_APP_LEAVER_CHECK_URL,
        group: 'Administration',
      },
      {
        name: 'Staff Onboarding',
        description: 'Onboard a new starter — create or pick the account, assign a licence, grant access, the 14-day BrightHR bridge and all-staff lists. Full-admin only.',
        icon: UserPlusIcon,
        url: '/it/onboarding',
        group: 'Administration',
        headerKey: 'itAdministration',
      },
      {
        name: 'Leaver Management',
        description: 'Offboard leavers — convert mailbox to shared, remove licences, block sign-in. Full-admin / IT Administration only.',
        icon: UserMinusIcon,
        url: process.env.REACT_APP_LEAVERS_URL,
        group: 'Administration',
        headerKey: 'itAdministration',
      },
      // Live reads from the two agent consoles. Internal dashboard routes, so
      // literal paths rather than env vars — the same shape as the Device
      // Register card over in Logistics.
      {
        name: 'Atera Agents',
        description: 'Every machine reporting into Atera, read live on each refresh — confirm an enrolment landed without signing into Atera.',
        icon: ComputerDesktopIcon,
        url: '/devices/atera',
        group: 'Endpoint Agents',
        headerKey: 'itAdministration',
      },
      {
        name: 'Wazuh Agents',
        description: 'Every endpoint enrolled in Wazuh, read live on each refresh — a new agent appears as soon as it checks in.',
        icon: ShieldCheckIcon,
        url: '/devices/wazuh',
        group: 'Endpoint Agents',
        headerKey: 'itAdministration',
      },
    ],
  },
  {
    key: 'infosec',
    name: 'Infosec',
    fullName: 'Infosec',
    icon: LockClosedIcon,
    description: 'ISO 27001 compliance evidence and access review history.',
    items: [
      {
        name: 'Statement of Applicability',
        description: 'All 93 Annex A controls — applicability, ownership, status, and evidence.',
        icon: ClipboardDocumentCheckIcon,
        url: process.env.REACT_APP_SOA_URL,
      },
      {
        name: 'ISO 27001 ISMS Tracker',
        description: 'Readiness dashboard, mandatory clauses 4–10, and audit register.',
        icon: ClipboardDocumentCheckIcon,
        url: process.env.REACT_APP_ISO_TRACKER_URL,
      },
      {
        name: '365 Access Review History',
        description: 'Weekly point-in-time snapshots of the 365 directory, kept for audit evidence.',
        icon: ClockIcon,
        url: process.env.REACT_APP_ACCESS_REVIEW_HISTORY_URL,
      },
    ],
  },
  {
    key: 'hr',
    name: 'HR',
    fullName: 'HR (Human Resources)',
    icon: UserGroupIcon,
    description: 'People data and HR case management.',
    items: [
      {
        name: 'HR Cases',
        description: 'Disciplinary, grievance and investigation cases — workflow, chronology and evidence.',
        icon: ClipboardDocumentListIcon,
        url: process.env.REACT_APP_HR_CASES_URL,
        headerKey: 'hrAdministration',
      },
      {
        // Web/HR side of the engineer-facing FSM absence flow. Gated to HR
        // Administration (same group as HR Cases) — the dashboard proxy
        // enforces it server-side too.
        name: 'Absence & Late Reporting',
        description: 'See who has been reported late or absent today, or across any date range, and act on it.',
        icon: CalendarDaysIcon,
        url: '/absence-reporting',
        headerKey: 'hrAdministration',
      },
      {
        // Staff confirm or correct their home address (01/10/2026); HR sees
        // BrightHR vs Nano side by side. Same gate as HR Cases.
        name: 'Address Confirmations',
        description: 'Who has confirmed or corrected their home address, with BrightHR and Nano side by side.',
        icon: MapPinIcon,
        url: '/infosec/address-confirmations',
        headerKey: 'hrAdministration',
      },
      {
        // Live ActivTrak (nothing stored). Own group, not HR Administration.
        name: 'Activity Monitor',
        description: 'Live ActivTrak activity: team hours, per-person apps, sites, timeline and alarms.',
        icon: ComputerDesktopIcon,
        url: '/infosec/activity-monitor',
        headerKey: 'hrActivityMonitoring',
      },
    ],
  },
  // Managers (06/10/2026) — tools for office managers, one header group per
  // card. Department gated on the Managers group; Absence Reporting on its
  // own group, enforced server-side by FSM (reporter's own records only).
  {
    key: 'managers',
    name: 'Managers',
    // Shown in the top section with Inbox/Directory/Helpdesk, not down in the
    // department list. Still a department: same gate, same card panel.
    pinned: true,
    fullName: 'Managers',
    icon: BriefcaseIcon,
    description: 'Tools for managing your team.',
    items: [
      {
        name: 'Absence Reporting',
        description: 'Report an absence or late arrival for your team, and complete their return to work.',
        icon: CalendarDaysIcon,
        url: '/managers/absence-reporting',
        headerKey: 'absenceReporting',
      },
    ],
  },
  {
    key: 'health-safety',
    name: 'Health & Safety',
    icon: ShieldCheckIcon,
    description: 'Safety, health, environment, and quality resources.',
    items: [
      // One card per nano-fibre-inspections route, grouped/gated to mirror
      // that app's own InspectionTypes.jsx dashboard exactly — Management
      // gates the 4 forms, Administration gates re-inspections/dashboards/
      // track changes, same headerKey names as that repo's
      // permissionGroups.js (kept in sync per the note at the top of this
      // app's own server/config/permissionGroups.js).
      {
        name: 'Harness',
        description: 'View and manage harness inspection records.',
        icon: UserIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/harness/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Ladder',
        description: 'View and manage ladder inspection records.',
        icon: Bars3Icon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/ladder/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Step Ladder',
        description: 'View and manage step ladder inspection records.',
        icon: ClipboardDocumentListIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/step-ladder/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Pre-Climb Audit',
        description: 'View and manage pre-climb audit records.',
        icon: ShieldCheckIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/pre-climb/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Safe Drilling Audit',
        description: 'View and manage safe drilling audit records.',
        icon: WrenchScrewdriverIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/pre-drill/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Warehouse Safety Checklist (SHEQ Only)',
        description: 'View and manage warehouse safety checklist records.',
        icon: ArchiveBoxIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/warehouse-safety/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Retrospective Audit',
        description: 'View and manage retrospective audit records.',
        icon: ClipboardDocumentCheckIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/retrospective-audit/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Live Audit - Overhead',
        description: 'View and manage live installation audit records for overhead work.',
        icon: SignalIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/installation-live-audit-overhead/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Live Audit - Underground',
        description: 'View and manage live installation audit records for underground work.',
        icon: WrenchScrewdriverIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/installation-live-audit-underground/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Vehicle Inspection',
        description: 'View and manage vehicle inspection records.',
        icon: TruckIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/vehicle/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'PPE & Equipment (MEWP)',
        description: 'View and manage PPE and equipment inspection records for MEWP work.',
        icon: ShieldCheckIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/ppe-equipment/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'PPE & Equipment (SHEQ)',
        description: 'View and manage PPE and equipment inspection records carried out by SHEQ.',
        icon: ShieldCheckIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/ppe-equipment-sheq/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'DSE Workstation',
        description: 'View and manage DSE workstation self-assessment records.',
        icon: ComputerDesktopIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/dse-workstation/inspections`,
        group: 'Health & Safety Management',
        headerKey: 'healthSafetyManagement',
      },
      {
        name: 'Re-Inspections',
        description: 'Equipment due for re-inspection, grouped by how soon.',
        icon: CalendarDaysIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/re-inspections`,
        group: 'Health & Safety Administration',
        headerKey: 'healthSafetyAdministration',
      },
      {
        name: 'Equipment Register',
        description: 'Every ladder and harness — where it is, and what its last inspection said.',
        icon: ArchiveBoxIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/equipment-register`,
        group: 'Health & Safety Administration',
        headerKey: 'healthSafetyAdministration',
      },
      {
        name: 'Equipment Compliance Dashboard',
        description: 'Historic pass/fail trends for Ladder and Harness inspections.',
        icon: ChartBarIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/equipment-dashboard`,
        group: 'Health & Safety Administration',
        headerKey: 'healthSafetyAdministration',
      },
      {
        name: 'Audit Activity Dashboard',
        description: 'Historic pass/fail trends for Pre-Climb and Safe Drilling audits.',
        icon: ClipboardDocumentCheckIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/audit-dashboard`,
        group: 'Health & Safety Administration',
        headerKey: 'healthSafetyAdministration',
      },
      {
        name: 'Track Changes',
        description: 'Every amendment made to an inspection or audit, for ISO review.',
        icon: ClockIcon,
        url: process.env.REACT_APP_INSPECTIONS_URL && `${process.env.REACT_APP_INSPECTIONS_URL}/track-changes`,
        group: 'Health & Safety Administration',
        headerKey: 'healthSafetyAdministration',
      },
    ],
  },
  {
    key: 'operations',
    name: 'Operations',
    fullName: 'Operations',
    icon: WrenchScrewdriverIcon,
    description: 'Field operations records and reporting.',
    items: [
      {
        // Office Functions (03/11/2026): office-side tools, each granted
        // person by person on Platform > Page access (pageKey) rather than by
        // an Entra group. Job Cards first.
        name: 'Job Cards',
        description: 'Every install job from ODIN: book, track and close them, import jobs and work the outstanding items.',
        icon: ClipboardDocumentListIcon,
        url: '/operations/jobcards',
        group: 'Office Functions',
        pageKey: 'jobcards-office',
      },
      {
        name: 'Assigning',
        description: 'Plan each day\'s routes: preview and confirm who goes where, set teams and caps, and place support requests.',
        icon: MapPinIcon,
        url: '/operations/assigning',
        group: 'Office Functions',
        pageKey: 'assigning',
      },
      {
        name: 'UTI Dashboard',
        description: 'Every unable-to-install job with its evidence and approval, and approving or moving it on.',
        icon: ExclamationTriangleIcon,
        url: '/operations/uti-dashboard',
        group: 'Office Functions',
        pageKey: 'uti-dashboard',
      },
      {
        name: 'Support Requests',
        description: 'Every support request engineers raised, with its approval and evidence, and raising one against a job.',
        icon: LifebuoyIcon,
        url: '/operations/support-requests',
        group: 'Office Functions',
        pageKey: 'support-requests',
      },
      {
        name: 'Bookings Calendar',
        description: 'Booked jobs per region and day against the Locked FNO target, and moving jobs between routes on the Daily view.',
        icon: CalendarDaysIcon,
        url: '/operations/jobcards/calendar',
        group: 'Office Functions',
        pageKey: 'bookings-calendar',
      },
      {
        name: 'Internal Remediations',
        description: 'Every internal remediation engineers reported, with its reasons, approval and evidence.',
        icon: WrenchScrewdriverIcon,
        url: '/operations/internal-remediations',
        group: 'Office Functions',
        pageKey: 'internal-remediations',
      },
      {
        name: 'Assistance Tracking',
        description: 'Every job where one engineer assisted another, and who helped whom.',
        icon: UserGroupIcon,
        url: '/operations/assistance-tracking',
        group: 'Office Functions',
        pageKey: 'assistance-tracking',
      },
      {
        name: 'Pole Survey',
        description: 'Openreach inspection record for a pole \u2014 climb status, hoist need, CBT and drop distances \u2014 before a job is sent.',
        icon: MapPinIcon,
        url: '/pole-survey',
        // Own header (19/10/2026): the map, its log and Climb Check together.
        // No headerKey here: the map stays open to all of Operations.
        group: 'Pole Surveys',
      },
      {
        // Pole survey audit log (TKT-00042): who surveyed which address, the
        // outcome and how long it took. Only Nano-Pole-Survey-Audit-Access.
        name: 'Survey Log',
        description: 'Every pole survey: who surveyed which address, the outcome, how long it took and what they checked.',
        icon: ClipboardDocumentCheckIcon,
        url: '/pole-survey/log',
        group: 'Pole Surveys',
        headerKey: 'poleSurveyAudit',
      },
      {
        // Climb Check: tomorrow's ODIN jobs surveyed for climb / hoist / route
        // via another pole. Pole survey managers only for now.
        name: 'Climb Check',
        description: 'Tomorrow’s jobs: climbable, hoist, or run the drop via another pole.',
        icon: ClipboardDocumentCheckIcon,
        url: '/climb-check',
        group: 'Pole Surveys',
        headerKey: 'poleSurveyAudit',
      },
      {
        // Job Imports (29/10/2026, on trial): ODIN's work orders read as a
        // logged-in ODIN user. Same people as Climb Check for now.
        name: 'Job Imports',
        description: 'Log in to ODIN and read its work orders with schedule, provider and position.',
        icon: ClipboardDocumentCheckIcon,
        url: '/job-imports',
        headerKey: 'poleSurveyAudit',
      },
      {
        name: 'Commercial Surveys',
        description: 'Raise and assign commercial surveys, and read what the surveyor sent back.',
        icon: ClipboardDocumentCheckIcon,
        url: '/commercial-surveys',
        headerKey: 'commercialSurveys',
      },
      {
        name: 'Engineer Directory',
        description: 'Source of truth for engineers — Region/ISP/Cluster/Banding/Reports-To, synced nightly from the Ops Match Us list.',
        icon: UserGroupIcon,
        url: process.env.REACT_APP_ENGINEERS_URL,
      },
      {
        name: 'Match-Ups',
        description: 'Editable engineer match-up list — region, daily target, on-capacity, ISP/cluster. The native source FDC reads.',
        icon: ClipboardDocumentCheckIcon,
        url: '/operations/match-ups',
        group: 'Capacity Planning',
        headerKey: 'operationsTools',
      },
      {
        name: 'FDC — Capacity Planning',
        description: 'Field Daily Capacity: engineer availability per region per day, with annual leave, weekend cover and temporary region moves.',
        icon: CalendarDaysIcon,
        url: '/operations/fdc',
        group: 'Capacity Planning',
        headerKey: 'operationsTools',
      },
      {
        // ODIN bookings against FDC capacity per region (20/10/2026). Same
        // Operations app and gate as FDC.
        name: 'ODIN Calendar',
        description: 'What ODIN has booked per region and day, set against FDC capacity. Link ODIN planners to regions on its mapping page.',
        icon: CalendarDaysIcon,
        url: '/operations/odin-calendar',
        group: 'Capacity Planning',
        headerKey: 'operationsTools',
      },
      {
        name: 'General SSRA Archive',
        description: 'General (non-type-specific) Site Specific Risk Assessment records migrated from SharePoint.',
        icon: ArchiveBoxIcon,
        url: process.env.REACT_APP_SSRA_ARCHIVE_URL,
      },
      {
        // Coordination Tools — a coordinator-facing header, gated by its own
        // Entra group. Daily Installations for now; the same report the
        // Reporting group carries, but reached by coordinators who don't have
        // the operations-reporting group. The tech /api/ops daily-installations
        // endpoints admit this group too.
        name: 'Daily Installations',
        description: 'Installation activity by day, region and engineer — with the stock each job actually consumed.',
        icon: ChartBarIcon,
        url: '/daily-installations',
        group: 'Coordination Tools',
        headerKey: 'coordinationTools',
      },
      {
        name: 'Operations Reporting',
        description: 'Where all nine operations reports stand this month, and a way into each.',
        icon: ChartBarIcon,
        url: '/ops-reporting',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Monthly Install Count',
        description: 'Completed installs from the start of the month by network, area and week.',
        icon: ChartBarIcon,
        url: '/install-count',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Stock Logging KPIs',
        description: 'How consistently each engineer and team logs the stock used on completed work.',
        icon: ChartBarIcon,
        url: '/stock-logging-kpis',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Installations Leaderboard',
        description: 'Engineers ranked on weighted completions, adjusted for assistance given and received.',
        icon: ChartBarIcon,
        url: '/installations-leaderboard',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Daily Installations',
        description: 'Installation activity by day, region and engineer — with the stock each job actually consumed.',
        icon: ChartBarIcon,
        url: '/daily-installations',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Target v Performance',
        description: 'Banded daily targets, progress against them, and the incentive payments that follow.',
        icon: ChartBarIcon,
        url: '/target-performance',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Installation Master',
        description: 'Jobs booked against jobs completed, the completion rate, and where the month lands.',
        icon: ChartBarIcon,
        url: '/installation-master',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Whereabouts Master',
        description: 'How the field force spent its days, and whether the work matched the people available.',
        icon: ChartBarIcon,
        url: '/whereabouts-master',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Support Requests',
        description: 'What the field asked for, what was approved, and who answered.',
        icon: ChartBarIcon,
        url: '/support-requests',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Internal Remediations',
        description: 'What is going back for rework, why, and who raised it.',
        icon: ChartBarIcon,
        url: '/internal-remediations',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Safety & Compliance',
        description: 'MEWP work approved without a site-specific risk assessment on record.',
        icon: ChartBarIcon,
        url: '/safety-compliance',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
      {
        name: 'Customer Experience',
        description: 'Trustpilot reviews by engineer, and the vouchers they earn.',
        icon: ChartBarIcon,
        url: '/customer-experience',
        group: 'Reporting',
        headerKey: 'operationsReporting',
      },
    ],
  },
  {
    key: 'communications',
    name: 'Communications',
    fullName: 'Communications',
    icon: PaperAirplaneIcon,
    description: 'Broadcast messages to staff and engineers, and track engagement.',
    items: [
      {
        name: 'Comms Dispatch',
        description: 'Compose and send messages to people, groups, or everyone — with read/acknowledgement tracking.',
        icon: PaperAirplaneIcon,
        // Internal dashboard route; the tech API gates it on NEO2-Communications-Access.
        url: '/comms',
      },
      {
        name: 'Message Analytics',
        description: 'Per-message delivery, read, and acknowledgement metrics.',
        icon: ChartBarIcon,
        url: '/comms/analytics',
      },
      {
        name: 'Recipient Activity',
        description: 'Per-person message history — what each recipient has read and acknowledged, and how quickly.',
        icon: UsersIcon,
        url: '/comms/recipients',
      },
    ],
  },
  {
    key: 'support',
    name: 'Support',
    fullName: 'Support',
    icon: LifebuoyIcon,
    description: 'Live assistance for engineers and the Nano Bot AI assistant.',
    items: [
      {
        name: 'Support Console',
        description: 'Live assistance for engineers — reply to help requests from the field in a chat thread; the engineer is notified in their app.',
        icon: LifebuoyIcon,
        // Internal dashboard route; the tech API gates it on Nano-Support-Access
        // or Platform > Page access 'support-console' (03/11/2026).
        url: '/support',
        pageKey: 'support-console',
        pageOrDepartment: true,
      },
      {
        name: 'AI Assistant Review',
        description: 'What engineers ask Nano Bot, what it could not answer, answers flagged as wrong, and answers to re-check after a procedure changes.',
        icon: SparklesIcon,
        // Internal dashboard route; the dashboard server gates it on Nano-Support-Access
        // or Platform > Page access 'assistant-review' (03/11/2026).
        url: '/support/assistant',
        pageKey: 'assistant-review',
        pageOrDepartment: true,
      },
    ],
  },
  {
    key: 'customer-experience',
    name: 'Customer Experience',
    fullName: 'Customer Experience',
    icon: MapPinIcon,
    description: 'The CX registers \u2014 what went wrong, where it happened, and what it cost.',
    items: [
      {
        name: 'AINM Register',
        description: 'Accidents, incidents and near misses, on a map of where they happened.',
        icon: ExclamationTriangleIcon,
        url: '/cx/ainm',
      },
      {
        name: 'Complaint Register',
        description: 'Customer complaints, on a map of where they came from.',
        icon: FaceFrownIcon,
        url: '/cx/complaints',
      },
    ],
  },
  {
    key: 'fleet',
    name: 'Fleet',
    fullName: 'Fleet',
    icon: TruckIcon,
    description: 'Vehicle compliance for the fleet, checked against the Fleet Master.',
    items: [
      {
        name: 'Walkarounds',
        description: 'Who has done this week\u2019s vehicle walkaround check and who has not, by van and assigned driver.',
        icon: ClipboardDocumentCheckIcon,
        url: '/walkarounds',
      },
      {
        name: 'Driver Licence Checks',
        description: 'Every driver\u2019s licence on file: images, DVLA summary, points, expiry and the quarterly check.',
        icon: IdentificationIcon,
        url: '/driver-licences',
      },
      {
        name: 'Fines & PCNs',
        description: 'Parking, crossing and speeding fines: who was driving, who paid and whether it is recharged.',
        icon: ReceiptPercentIcon,
        url: '/fines',
      },
      {
        name: 'Rental Van Inspections',
        description: 'On-hire and off-hire inspections of rental vans and MEWPs, with photos.',
        icon: CameraIcon,
        url: '/rental-inspections',
      },
      {
        name: 'Fleet Master Backup',
        description: 'Read-only nightly copy of the monday.com Fleet Master: every vehicle, its compliance dates, lease details and certificates.',
        icon: TruckIcon,
        url: '/fleet-master-backup',
      },
    ],
  },
  {
    key: 'logistics',
    name: 'Logistics',
    fullName: 'Logistics',
    icon: CubeIcon,
    description: 'Field stock counts and logistics tools.',
    items: [
      {
        name: 'ISP Stock Ordering',
        description: 'What to order: the last three weekly counts per ISP, average usage, and the shortfall rounded up to whole boxes.',
        icon: ArrowTrendingDownIcon,
        url: '/isp-stock-counts',
        group: 'Stock Management',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Stock Transfers',
        description: 'Stock handed between engineers\u2019 vans \u2014 who gave what to whom, and what is still waiting to be confirmed.',
        icon: ArrowsRightLeftIcon,
        url: '/van-transfers',
        group: 'Stock Management',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Stock Disputes',
        description: 'Stock logged off another engineer’s van, where the owner says it was not his.',
        icon: ScaleIcon,
        url: '/stock-disputes',
        group: 'Stock Management',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Job Card Stock',
        description: 'What engineers logged against their job cards - ISP and consumables, by item or by job.',
        icon: ClipboardDocumentListIcon,
        url: '/job-card-stock',
        group: 'Stock Management',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Job Card Stock Form Order',
        description: 'Arrange the order engineers see when logging stock against a job card.',
        icon: Bars3BottomLeftIcon,
        url: '/job-card-stock-order',
        group: 'Stock Management',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Van Stock',
        description: 'ISP stock per van — latest weekly count minus job-card usage.',
        icon: TruckIcon,
        url: '/van-stock',
        group: 'Stock Management',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Job Cards',
        description: 'Scheduler board — all install job cards for the day, live from SharePoint.',
        icon: CalendarDaysIcon,
        url: '/job-cards',
      },
      {
        name: 'Stock Procurement',
        description: 'Raise a stock order from the master catalog — sent to the Head of Logistics for approval.',
        icon: ShoppingCartIcon,
        url: '/procurement',
        group: 'Stock Procurement',
        headerKey: 'procurement',
      },
      {
        name: 'Procurement Approvals',
        description: 'Final-approve stock orders; assign suppliers and generate the purchase orders.',
        icon: ClipboardDocumentCheckIcon,
        url: '/procurement/approvals',
        group: 'Stock Procurement',
        headerKey: 'procurement',
      },
      {
        name: 'SKU Catalog',
        description: 'The master product list — prices, suppliers and Xero sync.',
        icon: CubeIcon,
        url: '/platform/products',
        group: 'Stock Procurement',
        headerKey: 'procurement',
      },
      {
        name: 'Goods Receiving',
        description: 'Book in deliveries against purchase orders and bring stock into inventory.',
        icon: InboxArrowDownIcon,
        url: '/procurement/receiving',
        group: 'Stock Procurement',
        headerKey: 'procurement',
      },
      {
        name: 'Back Orders',
        description: 'Ordered and not yet accepted — including anything that arrived damaged or was rejected.',
        icon: ClockIcon,
        url: '/procurement/back-orders',
        group: 'Stock Procurement',
        headerKey: 'procurement',
      },
      {
        name: 'Stock on Hand',
        description: 'Live inventory per storeroom, driven by goods receiving.',
        icon: ArchiveBoxIcon,
        url: '/procurement/inventory',
        group: 'Stock Procurement',
        headerKey: 'procurement',
      },
      {
        name: 'Submitted Stock Counts',
        description: 'Counts already submitted by the storerooms, across both regions.',
        icon: CubeIcon,
        url: process.env.REACT_APP_STOCK_COUNTS_URL,
        group: 'Weekly ISP Count',
        // Matches nano-fibre-logistics' own RequireGroup gate on this page
        // (headerKey="inventory-ops") — only shown here if the user could
        // actually get past that gate, not just because the department is
        // visible to them.
        headerKey: 'inventory-ops',
      },
      {
        // Singular: the two per-region forms were merged into one combined form
        // (2026-07-21), where regions are a selection rather than separate
        // pages.
        name: 'Weekly ISP Stock Count',
        description: 'Submit a stock count for Region N, Region B or Remediation Stock.',
        icon: CubeIcon,
        // Deep-links to the form itself. It previously pointed at the logistics
        // root, which landed on that app's home screen and left the user to
        // find the form — the card names a form, so it should open one.
        url: process.env.REACT_APP_LOGISTICS_URL && `${process.env.REACT_APP_LOGISTICS_URL}/stock-count`,
        group: 'Weekly ISP Count',
        // Nav-visibility only (2026-07-14) — the underlying page in
        // nano-fibre-logistics still has no RequireGroup gate (any
        // department member, or NEO2-FSM-Stock-Access via FSM, can submit a
        // count regardless of this header). This just hides the dashboard
        // sidebar link from Logistics members who aren't in Inventory &
        // Operations, since field engineers reach the forms via FSM, not
        // this dashboard.
        headerKey: 'inventory-ops',
      },
      {
        // One SKU Manager (27/10/2026) replaces the separate ISP and Storeroom
        // SKU Manager cards: ISP, the storeroom lists, suppliers and Nano
        // Utilities all live on the one logistics page.
        name: 'SKU Manager',
        description: 'Every SKU in one place: ISP, storeroom lists, suppliers and Nano Utilities.',
        icon: CubeIcon,
        url: process.env.REACT_APP_LOGISTICS_URL && `${process.env.REACT_APP_LOGISTICS_URL}/sku-manager`,
        group: 'SKU Manager',
        headerKey: 'inventory-ops',
      },
      {
        // The monthly storeroom count is a separate exercise from the weekly
        // ISP count above — different form, different cadence, different
        // people — so it gets its own group rather than a tenth card in
        // Stock Management. It had no card here at all until now, which is
        // why it could only be reached from the logistics app's own home.
        name: 'Storeroom Count',
        description: 'Submit the monthly count for a storeroom — consumables, tools, uniform & PPE and civil.',
        icon: ArchiveBoxIcon,
        url: process.env.REACT_APP_LOGISTICS_URL && `${process.env.REACT_APP_LOGISTICS_URL}/storeroom-count`,
        group: 'Monthly Storeroom Count',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Storeroom Counts Dashboard',
        description: 'Submitted monthly storeroom counts — open a count for its full breakdown, signature and export.',
        icon: ClipboardDocumentCheckIcon,
        url: process.env.REACT_APP_LOGISTICS_URL && `${process.env.REACT_APP_LOGISTICS_URL}/storeroom-counts`,
        group: 'Monthly Storeroom Count',
        headerKey: 'inventory-ops',
      },
      {
        name: 'SIM Manager',
        description: "Assign and change company mobile numbers. Feeds the contact number shown in the company directory.",
        icon: DevicePhoneMobileIcon,
        // An internal dashboard route, not another module's URL — hence a
        // literal path rather than an env var. It renders as a normal link, so
        // it needs no special handling in ModulePanel.
        url: '/sims',
        // Grouped with the other stock/asset tools. Deliberately no headerKey:
        // the API gates this on the Logistics department, so gating the card on
        // inventory-ops as its siblings do would hide it from Logistics members
        // who can in fact use it.
        group: 'Asset Management',
        headerKey: 'assetManagement',
      },
      {
        // Requested-vs-picked is the reason this exists: FSM shows an engineer
        // his own orders and the storeroom its open queue, so a collected order
        // was visible to nobody once it left the counter.
        // One screen for both, because the question is "where is this item",
        // and having to navigate elsewhere to check the other side makes that
        // harder than it needs to be.
        name: 'Stock Check',
        description: 'For when an engineer says his stock is wrong — the opening count and every movement since, job by job.',
        icon: MagnifyingGlassIcon,
        url: '/stock-check',
        group: 'Stock',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Stock Variances',
        description: 'Where a van count did not match its books: every difference between one count and the next, with an outcome recorded for each.',
        icon: ScaleIcon,
        url: '/stock-variances',
        group: 'Stock',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Stock Analytics',
        description: 'Charts of stock moving in, out and onto job cards — by day, by item and by storeroom.',
        icon: ChartBarIcon,
        url: '/stock-analytics',
        group: 'Stock',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Stock Levels',
        description: 'What a storeroom holds on the ledger, or what a van is carrying — derived from its last count plus receipts, minus job-card usage.',
        icon: ArchiveBoxIcon,
        url: '/stock-levels',
        group: 'Stock',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Stock Reset',
        description: 'Make a physical count the opening balance for a van or storeroom. Usage since the count still lands on the next order.',
        icon: ArrowPathIcon,
        url: '/stock-reset',
        group: 'Stock',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Van Stock Orders',
        description: 'Every order engineers have raised and what the storeroom picked against it — flags anything picked above what was asked for.',
        icon: TruckIcon,
        url: '/van-orders',
        group: 'Stock',
        headerKey: 'inventory-ops',
      },
      {
        name: 'Device Register',
        description: 'Company laptops and desktops — provisioning checklist, who holds what, and returns. The ISO 27001 asset-provisioning evidence.',
        icon: ComputerDesktopIcon,
        url: '/devices',
        group: 'Asset Management',
        headerKey: 'assetManagement',
      },
      {
        name: 'Phone Register',
        description: 'Company phones and tablets, synced nightly from Miradore (MDM) — who holds what, serial/IMEI, OS and last check-in.',
        icon: DeviceTabletIcon,
        url: '/phones',
        group: 'Asset Management',
        headerKey: 'assetManagement',
      },
      {
        name: 'Devices to Collect',
        description: 'Company laptops and desktops still held by people being offboarded — mark them collected as the kit comes back.',
        icon: InboxArrowDownIcon,
        url: '/devices/to-collect',
        group: 'Asset Management',
        headerKey: 'assetManagement',
      },
    ],
  },
  {
    key: 'fsm',
    name: 'FSM',
    fullName: 'Field Service Mobile',
    icon: DevicePhoneMobileIcon,
    description: 'Engineer-facing field portal — stock counts, inspections.',
    // Field engineers whose ONLY department is this one never see this
    // card at all — AuthenticatedLayout.jsx redirects them straight to FSM
    // before the dashboard shell renders. This entry exists for office
    // staff who also have FSM alongside another department.
    items: [
      {
        name: 'Open FSM',
        description: 'Field Service Mobile — the engineer-facing portal.',
        icon: DevicePhoneMobileIcon,
        url: process.env.REACT_APP_FSM_URL,
      },
    ],
  },
  {
    key: 'training',
    name: 'Training',
    fullName: 'Training',
    icon: AcademicCapIcon,
    description: 'Build and assign staff training courses.',
    // Course-*taking* ("My Learning") is deliberately NOT here — it's the
    // ungated ALWAYS_VISIBLE_ITEMS entry above, since it applies to every
    // employee, not just Training dept members. This card is admin-only.
    items: [
      {
        name: 'Training Matrix',
        description: 'Staff accreditations, expiry dates, and certificates.',
        icon: ClipboardDocumentCheckIcon,
        url: process.env.REACT_APP_TRAINING_MATRIX_URL || null,
        headerKey: 'trainingMatrix',
        group: 'Training Records',
      },
      {
        name: 'Course Builder',
        description: 'Author courses, assign them to staff, and review results.',
        icon: WrenchScrewdriverIcon,
        url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/course-builder` : null,
        // Course Administration members see this too, without needing
        // separate direct membership — see permissionsService.js's
        // getMyAccess, which adds 'courseManagement' automatically for
        // courseAdmin members.
        headerKey: 'courseManagement',
        group: 'Course Management',
      },
      {
        name: 'Dispatch Manager',
        description: 'Assign courses to people, bulk-dispatch, and send reminders.',
        icon: PaperAirplaneIcon,
        url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/dispatch` : null,
        // Narrower than courseManagement, same header as Course Insights/
        // History below — see permissionGroups.js's headers[] entry and
        // nano-fibre-training's own dispatch/analytics routes for why.
        headerKey: 'courseAdmin',
        group: 'Course Administration',
      },
      {
        name: 'Course Insights',
        description: 'Per-course completion/fail stats — who still needs to finish, who has.',
        icon: ChartBarIcon,
        url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/insights` : null,
        headerKey: 'courseAdmin',
        group: 'Course Administration',
      },
      {
        name: 'Course History',
        description: 'Who changed what, and when — a per-course audit trail.',
        icon: ClockIcon,
        url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/audit-log` : null,
        headerKey: 'courseAdmin',
        group: 'Course Administration',
      },
    ],
  },
  // Finance — a read-only slice of the operations reports. Whole department and
  // its one section are gated on Nano-Finance-Access (department key + the
  // 'finance' header, same group). Reuses the existing report routes; the tech
  // report API accepts the finance group alongside operationsReporting.
  {
    key: 'finance',
    name: 'Finance',
    fullName: 'Finance',
    icon: ChartBarIcon,
    description: 'Installation reporting for the finance team.',
    items: [
      {
        name: 'Monthly Install Count',
        description: 'Completed installs from the start of the month by network, area and week.',
        icon: ChartBarIcon,
        url: '/install-count',
        group: 'Installations Reporting',
        headerKey: 'finance',
      },
      {
        name: 'Daily Installations',
        description: 'Installation activity by day, region and engineer — with the stock each job actually consumed.',
        icon: ChartBarIcon,
        url: '/daily-installations',
        group: 'Installations Reporting',
        headerKey: 'finance',
      },
      {
        name: 'Installation Master',
        description: 'Jobs booked against jobs completed, the completion rate, and where the month lands.',
        icon: ChartBarIcon,
        url: '/installation-master',
        group: 'Installations Reporting',
        headerKey: 'finance',
      },
    ],
  },
// Items whose URL env var is unset are dropped — that is how a module absent
// from an environment disappears instead of linking somewhere that isn't
// there. A department left with nothing then disappears too: listing it would
// open an empty panel, which reads as a broken page rather than as "not
// deployed here". Health & Safety is entirely inspections-backed, so on
// production (no inspections deployment) it drops out completely.
].map((dept) => ({ ...dept, items: dept.items.filter((i) => i.url) }))
  .filter((dept) => dept.items.length > 0);

// Deny-by-default (changed 2026-07-11): a department is visible ONLY if the
// user is a member of its configured Entra group. There is no more
// "ungated = open" exception — every department must have a group in
// nano-fibre-tech/server/config/permissionGroups.js, or nobody sees it at
// all (including its own admins, until they add themselves as a member).
// A card granted person by person (pageKey, Platform > Page access) also opens
// its department - showing only those cards (see ModulePanel).
export function holdsPageCard(dept, access) {
  return dept.items.some((i) => i.pageKey && access?.pages?.[i.pageKey]);
}

export function filterDepartmentsByAccess(departments, access) {
  if (!access) return [];
  return departments.filter((dept) => access.departments.includes(dept.key) || holdsPageCard(dept, access));
}

// Platform is NOT a department — it's the platform's own internals (which
// external systems we talk to, when they last ran, what they picked up), and
// it's for full admins only, deliberately outside the department model.
//
// Inferred from "can see every department", which is exactly what the server
// returns for a full admin. Checked against the configured list rather than a
// hardcoded count so adding a department doesn't silently widen access. This is
// UX only — the real gate is server-side on every /api/platform request.
export function isFullAdmin(access) {
  if (!access?.departments?.length) return false;
  return DEPARTMENTS.every((dept) => access.departments.includes(dept.key));
}

const PLATFORM_ITEMS = [
  { name: 'Integrations', icon: SignalIcon, path: '/platform/integrations' },
  { name: 'Resources', icon: CpuChipIcon, path: '/platform/resources' },
  { name: 'Backups', icon: ArchiveBoxIcon, path: '/platform/backups' },
  // Who can use each per-person page (Job Cards first) — the Nano Utilities model.
  { name: 'Page access', icon: KeyIcon, path: '/platform/page-access' },
];

// `open`/`onClose` drive the mobile off-canvas drawer. Below lg the sidebar is
// a fixed slide-in panel over a backdrop; at lg+ it's a normal static column
// (always visible, translate/backdrop no-op).
// `collapsed` (lg+ only, 19/10/2026): an icon-only rail; the mobile drawer is
// always full width. The layout owns and remembers it.
function Sidebar({
  selected, onSelect, departments, isLoading, open = false, onClose = () => {}, showPlatform = false,
  collapsed = false, onToggleCollapsed = null,
}) {
  const { instance } = useMsal();

  // React Router's location, NOT window.location: the latter is read during
  // render and never tells React it changed, so a client-side navigation can
  // leave the previous item highlighted. One value, shared by both lists.
  const { pathname } = useLocation();

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  // Contrast (Ruan, 19/10/2026): the sidebar sits on light purple and the
  // selected department / page is solid brand purple with white text, so where
  // you are stands out.
  const navItem = (active) => `flex w-full items-center gap-3 rounded-md px-4 py-3 text-base font-medium transition-colors ${
    collapsed ? 'lg:justify-center lg:px-0' : ''
  } ${
    active ? 'bg-brand text-white shadow-sm' : 'text-gray-800 hover:bg-[#E4D3EB]'
  }`;
  // Labels stay for screen readers when collapsed; the title gives a tooltip.
  const label = (text) => <span className={collapsed ? 'lg:sr-only' : ''}>{text}</span>;
  const tip = (text) => (collapsed ? text : undefined);
  const navIcon = (active) => `h-7 w-7 ${active ? 'text-white' : 'text-brand'}`;

  const renderDepartment = (dept) => {
    const Icon = dept.icon;
    const isSelected = selected === dept.key;
    return (
      <button
        key={dept.key}
        type="button"
        onClick={() => {
          onSelect(isSelected ? null : dept.key);
          onClose();
        }}
        className={navItem(isSelected)}
        aria-current={isSelected ? 'true' : undefined}
        title={tip(dept.name)}
      >
        <Icon className={navIcon(isSelected)} />
        {label(dept.name)}
      </button>
    );
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={onClose} aria-hidden="true" />
      )}
      {/* h-dvh, NOT h-screen: 100vh is the LARGE mobile viewport (URL bar
          hidden), so the pinned Logout footer sat underneath the browser
          address bar and was unreachable. 100dvh tracks the visible
          viewport instead. inset-y-0 is the fallback if dvh is unsupported. */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-dvh w-72 shrink-0 flex-col ${collapsed ? 'lg:w-20' : ''} border-r border-[#E0CFE7] bg-brand-light transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
      <div className={`relative px-4 text-center ${collapsed ? 'py-6 lg:px-2 lg:py-4' : 'py-6'}`}>
        <img src={`${process.env.PUBLIC_URL}/nano-fibre-logo.svg`} alt="Nano Fibre UK" className={`mx-auto h-auto ${collapsed ? 'w-3/5 lg:w-12' : 'w-3/5'}`} />
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1 text-gray-500 hover:bg-[#E4D3EB] lg:hidden"
          aria-label="Close menu"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <nav className={`flex-1 space-y-1 overflow-y-auto p-3 ${collapsed ? 'lg:px-2' : ''}`}>
        {(() => {
          const Icon = INBOX_ROUTE.icon;
          const isActive = pathname === INBOX_ROUTE.path;
          return (
            <a
              href={INBOX_ROUTE.path}
              title={tip(INBOX_ROUTE.name)}
              onClick={onClose}
              className={navItem(isActive)}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className={navIcon(isActive)} />
              {label(INBOX_ROUTE.name)}
            </a>
          );
        })()}

        {ALWAYS_VISIBLE_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <a
              key={item.name}
              title={tip(item.name)}
              href={item.url}
              className={navItem(false)}
            >
              <Icon className={navIcon(false)} />
              {label(item.name)}
            </a>
          );
        })}

        {ALWAYS_VISIBLE_ROUTES.map((item) => {
          const Icon = item.icon;
          const isActive = pathname === item.path;
          return (
            <a
              key={item.name}
              title={tip(item.name)}
              href={item.path}
              onClick={onClose}
              className={navItem(isActive)}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className={navIcon(isActive)} />
              {label(item.name)}
            </a>
          );
        })}

        {departments.filter((dept) => dept.pinned).map(renderDepartment)}

        <div className="mx-4 my-2 h-px bg-[#E0CFE7]" />

        {isLoading && <p className={`px-4 py-2 text-sm text-gray-400 ${collapsed ? 'lg:hidden' : ''}`}>Loading access...</p>}
        {departments.filter((dept) => !dept.pinned).map(renderDepartment)}


        {/* Platform — full admins only, and visually separated from the
            departments above because it isn't one. */}
        {showPlatform ? (
          <>
            <div className="mx-4 my-2 h-px bg-[#E0CFE7]" />
            <p className={`px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-brand-dark/70 ${collapsed ? 'lg:hidden' : ''}`}>Platform</p>
            {PLATFORM_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.path;
              return (
                <a
                  key={item.name}
                  title={tip(item.name)}
                  href={item.path}
                  onClick={onClose}
                  className={navItem(isActive)}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className={navIcon(isActive)} />
                  {label(item.name)}
                </a>
              );
            })}
          </>
        ) : null}
      </nav>

      <div className={`space-y-1 border-t border-[#E0CFE7] p-3 ${collapsed ? 'lg:px-2' : ''}`}>
        {onToggleCollapsed ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            className={`hidden lg:flex ${navItem(false).replace('flex w-full', 'w-full')}`}
            aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand the sidebar' : undefined}
          >
            {collapsed ? <ChevronDoubleRightIcon className={navIcon(false)} /> : <ChevronDoubleLeftIcon className={navIcon(false)} />}
            {label('Collapse')}
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleLogout}
          title={tip('Logout')}
          className={navItem(false)}
        >
          <ArrowLeftStartOnRectangleIcon className={navIcon(false)} />
          {label('Logout')}
        </button>
      </div>
      </aside>
    </>
  );
}

export default Sidebar;
